import React, { useEffect, useState } from "react";
import { Camera, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import SubmitCampsitePhotoButton from "./SubmitCampsitePhotoButton";

/**
 * SubmitPhotoPrompt — a friendly inline nudge on the campsite detail page
 * inviting signed-in visitors to upload a photo from their trip.
 *
 * Shown ONLY when:
 *   - The user is signed in (no point bugging anonymous visitors)
 *   - No user-uploaded photo already exists for this site (i.e. only stock
 *     or Wikimedia photos so far)
 *   - The visitor hasn't dismissed it for this campsite in the last 30 days
 *
 * Dismissal is stored in localStorage under
 * `ccu:submit-photo-prompt:dismissed:{campsiteId}` with an ISO timestamp;
 * we re-show after 30 days so repeat visitors get nudged again.
 */
const DISMISS_KEY = (campsiteId) => `ccu:submit-photo-prompt:dismissed:${campsiteId}`;
const DISMISS_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export default function SubmitPhotoPrompt({ campsiteId, campsiteName, images = [] }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!campsiteId) return;
    const hasUserPhoto = (images || []).some(
      (img) => img && typeof img === "object" && img.source === "user"
    );
    if (hasUserPhoto) {
      setVisible(false);
      return;
    }
    try {
      const raw = localStorage.getItem(DISMISS_KEY(campsiteId));
      if (raw) {
        const ts = parseInt(raw, 10);
        if (Number.isFinite(ts) && Date.now() - ts < DISMISS_MS) {
          setVisible(false);
          return;
        }
      }
    } catch {
      // localStorage may throw in some private-browsing contexts — fail open
    }
    setVisible(true);
  }, [campsiteId, images]);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY(campsiteId), String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const onSignIn = () => {
    nav(`/login?next=${encodeURIComponent(`/campsites/${campsiteId}?upload=1`)}`);
  };

  if (!visible) return null;

  return (
    <div
      className="relative mb-10 max-w-2xl border-2 border-[#ffb074] bg-gradient-to-br from-[#fff4ee] to-white rounded-2xl p-4 sm:p-5 shadow-sm"
      data-testid="submit-photo-prompt"
    >
      <button
        type="button"
        onClick={dismiss}
        className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-[#ff4500]/10 text-[#7a4f00] transition-colors"
        aria-label="Dismiss"
        data-testid="submit-photo-prompt-dismiss"
      >
        <X className="w-3.5 h-3.5" strokeWidth={2} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="hidden sm:flex w-11 h-11 rounded-xl bg-[#ff4500] text-white items-center justify-center flex-shrink-0 shadow-sm">
          <Camera className="w-5 h-5" strokeWidth={2} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="eyebrow text-[#ff4500] flex items-center gap-1.5 mb-1">
            <Camera className="w-3 h-3 sm:hidden" />
            Been here?
          </div>
          <h3 className="font-display text-lg sm:text-xl leading-tight mb-1">
            Show campers what {campsiteName} really looks like
          </h3>
          <p className="text-sm text-[#555] mb-3">
            Add a single landscape photo from your trip — pitch, view, sunset, anything but people.
            We&rsquo;ll add it to the gallery after a quick moderation check.
          </p>
          {user ? (
            <SubmitCampsitePhotoButton
              campsiteId={campsiteId}
              campsiteName={campsiteName}
              className="!bg-[#ff4500] !text-white !border-[#ff4500] !rounded-xl hover:!bg-[#e63e00] hover:!border-[#e63e00] hover:!text-white !px-4 !py-2.5 !text-xs !shadow-sm"
            />
          ) : (
            <button
              type="button"
              onClick={onSignIn}
              className="inline-flex items-center gap-2 text-xs mono uppercase tracking-wider px-4 py-2.5 bg-[#ff4500] text-white border border-[#ff4500] rounded-xl hover:bg-[#e63e00] transition-colors shadow-sm"
              data-testid="submit-photo-prompt-signin"
            >
              <Camera className="w-3.5 h-3.5" strokeWidth={2} />
              Sign in to add a photo
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
